import { useState, useEffect, useRef } from "react";
import { useSendMoneyContext, useServiceFailureContext } from "@zenith/context";
import { LimitsApiService, WalletApiService } from "@zenith/services/user";
import { DeviceSwitchLimitDTO } from "@zenith/services/user/models/DeviceSwitchLimitDTO";
import {
  BankApiService,
  CategoryApiService,
  // Beneficiary name enquiry is answered by transaction-service for every destination: Finopay
  // accounts (wallets and virtual accounts) locally, other banks by falling through to the bank.
  WalletApiService as TransactionWalletApiService,
} from "@zenith/services/transactions";
import {
  FormDataType,
  FormErrorsType,
  TransferType,
  WalletAccount,
  BankAccount,
} from "../types";
import {
  INITIAL_FORM_DATA,
  INITIAL_FORM_ERRORS,
  ERROR_MESSAGES,
  TRANSFER_OPTIONS,
} from "../constants";
import {
  MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH,
  NO_BENEFICIARY_WAS_FOUND_ERROR_CODE,
  FINOPAY_NIGERIA_BANK_CODE,
  WALLET,
} from "@zenith/data/constants";
import { validateWalletAccountFormat } from "@zenith/utils/Validations/wallet-validation";
import { formatCurrency } from "@zenith/utils";

export const useSendMoneyForm = () => {
  const { sendMoneyContext, setSendMoneyContext } = useSendMoneyContext();
  const [formData, setFormData] = useState<FormDataType>(INITIAL_FORM_DATA);
  const [formErrors, setFormErrors] =
    useState<FormErrorsType>(INITIAL_FORM_ERRORS);
  const [bankList, setBankList] = useState<{ value: string; label: string }[]>(
    []
  );
  const [isLoadingBanks, setIsLoadingBanks] = useState(true);
  const [bankListError, setBankListError] = useState(false);
  const [categoryList, setCategoryList] = useState<
    { value: string; label: string }[]
  >([]);
  const [availableLimit, setAvailableLimit] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [accountNumber, setAccountNumber] = useState("");
  const [transferOption, setTransferOption] = useState<TransferType>("bank");
  const [nameEnquiryRef, setNameEnquiryRef] = useState<string | undefined>(
    sendMoneyContext.nameEnquiryRef || undefined
  );
  const [validatedAccount, setValidatedAccount] = useState<
    { accountNumber: string; bankId: string } | undefined
  >(
    sendMoneyContext.nameEnquiryRef &&
      sendMoneyContext.beneficiaryAccountNumber &&
      sendMoneyContext.bankId
      ? {
          accountNumber: sendMoneyContext.beneficiaryAccountNumber,
          bankId: sendMoneyContext.bankId,
        }
      : undefined
  );
  const [saveBeneficiary, setSaveBeneficiary] = useState(false);
  const [deviceSwitchLimit, setDeviceSwitchLimit] = useState<
    DeviceSwitchLimitDTO | undefined
  >(undefined);
  const { setServiceFailureContext } = useServiceFailureContext();
  const currentAccountNumberRef = useRef(formData.beneficiaryAccountNumber);
  currentAccountNumberRef.current = formData.beneficiaryAccountNumber;

  useEffect(() => {
    loadLimits();
    loadBanks();
    loadCategories();
    loadDeviceSwitchLimit();
  }, []);

  const loadDeviceSwitchLimit = async () => {
    try {
      const response = await WalletApiService.getWalletDetails();
      const limit = response.data?.limits?.deviceSwitchLimit;
      if (limit?.expiryDateTime) {
        setDeviceSwitchLimit(limit);
      }
    } catch {
      // informational banner — silent fail is acceptable
    }
  };

  const loadLimits = async () => {
    try {
      const limitsResponse = await LimitsApiService.getAccountStatus();
      if (limitsResponse.data) {
        setAvailableLimit(
          Number(limitsResponse.data?.debit?.availableLimit ?? 0)
        );
        setAvailableBalance(
          Number(limitsResponse.data?.balance?.availableBalance ?? 0)
        );
        setAccountNumber(limitsResponse.data?.accountNumber || "");
      }
    } catch (error) {
      console.error("Error loading limits:", error);
    }
  };

  const loadBanks = async () => {
    setIsLoadingBanks(true);
    setBankListError(false);
    try {
      const banksResponse = await BankApiService.getBanks();
      if (banksResponse.data) {
        setBankList(
          banksResponse.data.map((bank) => ({
            value: bank.id.toString(),
            label: bank.bankName,
          }))
        );
      } else {
        setBankListError(true);
      }
    } catch (error) {
      console.error("Error loading banks:", error);
      setBankListError(true);
    } finally {
      setIsLoadingBanks(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesResponse = await CategoryApiService.getCategories();
      if (categoriesResponse.data) {
        setCategoryList(
          categoriesResponse.data.map((category) => ({
            value: category.id.toString(),
            label: category.name,
          }))
        );
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const validateAccountNumber = async (
    inputAccountNumber: string
  ): Promise<boolean> => {
    try {
      // Basic validation
      if (!inputAccountNumber) {
        setFormErrors((prev) => ({
          ...prev,
          beneficiaryAccountNumber: {
            hasError: true,
            message: ERROR_MESSAGES.ACCOUNT_NUMBER_REQUIRED,
          },
        }));
        return false;
      }

      if (inputAccountNumber.length !== MAX_BENEFICIARY_ACCOUNT_NUMBER_LENGTH) {
        setFormErrors((prev) => ({
          ...prev,
          beneficiaryAccountNumber: {
            hasError: true,
            message: ERROR_MESSAGES.ACCOUNT_NUMBER_INVALID,
          },
        }));
        return false;
      }

      if (accountNumber && inputAccountNumber === accountNumber) {
        setFormErrors((prev) => ({
          ...prev,
          beneficiaryAccountNumber: {
            hasError: true,
            message: ERROR_MESSAGES.SAME_ACCOUNT_TRANSFER,
          },
        }));
        return false;
      }

      // Check if we're in wallet transfer mode
      if (transferOption === TRANSFER_OPTIONS.WALLET) {
        const walletValidation =
          validateWalletAccountFormat(inputAccountNumber);
        if (!walletValidation.isValid) {
          setFormErrors((prev) => ({
            ...prev,
            beneficiaryAccountNumber: {
              hasError: true,
              message: ERROR_MESSAGES.INVALID_WALLET,
            },
          }));
          return false;
        }

        // Validate wallet account
        const response = await TransactionWalletApiService.getWalletHolderInfo(
          inputAccountNumber
        );
        const accountOwner = response.data?.accountOwner;

        if (response.success && accountOwner) {
          const walletData: WalletAccount = {
            accountNumber: inputAccountNumber,
            accountOwner,
          };
          // The service returns the account number as the reference for Finopay destinations,
          // which is what the transfer substitutes anyway; keep the fallback for older responses.
          const nameEnquiryRef =
            response.data?.nameEnquiryRef ?? walletData.accountNumber;

          if (!isStillCurrentAccountNumber(inputAccountNumber)) return false;

          setFormData((prev) => ({
            ...prev,
            beneficiaryName: walletData.accountOwner,
            bankName: WALLET.BANK_NAME,
            bankId: FINOPAY_NIGERIA_BANK_CODE,
          }));
          setSendMoneyContext({
            ...sendMoneyContext,
            beneficiaryName: walletData.accountOwner,
            bankName: WALLET.BANK_NAME,
            bankId: FINOPAY_NIGERIA_BANK_CODE,
            beneficiaryAccountNumber: walletData.accountNumber,
            transferType: WALLET.TRANSFER_TYPE,
            nameEnquiryRef,
          });
          setNameEnquiryRef(nameEnquiryRef);
          setValidatedAccount({
            accountNumber: walletData.accountNumber,
            bankId: FINOPAY_NIGERIA_BANK_CODE,
          });

          clearAccountNumberError();
          return true;
        }

        if (response.success === false && response.internalCode) {
          handleBeneficiaryError({
            body: {
              message: response.message,
              internalCode: response.internalCode,
            },
          });
          return false;
        }

        handleBeneficiaryError({
          body: {
            internalCode: NO_BENEFICIARY_WAS_FOUND_ERROR_CODE,
          },
        });
        return false;
      }

      // Handle bank transfer
      if (!formData.bankId) {
        clearAccountNumberError();
        return false;
      }

      // Same endpoint as the wallet option, with the selected bank: a Finopay account (including a
      // dynamic virtual account) is resolved locally instead of round-tripping through the bank.
      const response = await TransactionWalletApiService.getWalletHolderInfo(
        inputAccountNumber,
        formData.bankId
      );

      if (response.success && response.data?.accountOwner) {
        const bankData: BankAccount = {
          accountNumber: inputAccountNumber,
          bankId: formData.bankId,
          bankName: formData.bankName,
          beneficiaryName: response.data.accountOwner,
          nameEnquiryRef: response.data.nameEnquiryRef,
        };

        if (!isStillCurrentAccountNumber(inputAccountNumber)) return false;

        setFormData((prev) => ({
          ...prev,
          beneficiaryName: bankData.beneficiaryName,
        }));
        setSendMoneyContext({
          ...sendMoneyContext,
          beneficiaryName: bankData.beneficiaryName,
          beneficiaryAccountNumber: bankData.accountNumber,
          transferType: "bank",
          nameEnquiryRef: bankData.nameEnquiryRef || "",
        });
        setNameEnquiryRef(bankData.nameEnquiryRef);
        setValidatedAccount({
          accountNumber: bankData.accountNumber,
          bankId: bankData.bankId,
        });

        clearAccountNumberError();
        return true;
      }

      // Surface the service's own reason (e.g. "Beneficiary account not found", or a connection
      // issue) rather than the generic copy, as the wallet option already does.
      if (response.success === false && response.internalCode) {
        handleBeneficiaryError({
          body: {
            message: response.message,
            internalCode: response.internalCode,
          },
        });
        return false;
      }

      handleBeneficiaryError({
        body: {
          internalCode: NO_BENEFICIARY_WAS_FOUND_ERROR_CODE,
        },
      });
      return false;
    } catch (error: any) {
      console.error("Error validating account number:", error);
      handleBeneficiaryError(error);
      return false;
    }
  };

  const isStillCurrentAccountNumber = (inputAccountNumber: string) =>
    currentAccountNumberRef.current === inputAccountNumber;

  const resetAccountValidation = () => {
    setNameEnquiryRef(undefined);
    setValidatedAccount(undefined);
  };

  const clearAccountNumberError = () => {
    setFormErrors((prev) => ({
      ...prev,
      beneficiaryAccountNumber: {
        hasError: false,
        message: "",
      },
    }));
  };

  const handleBeneficiaryError = (error: any) => {
    setFormData((prev) => ({
      ...prev,
      beneficiaryName: "",
    }));

    // Clear nameEnquiryRef on error
    setNameEnquiryRef(undefined);
    setValidatedAccount(undefined);

    if (
      error.body?.internalCode &&
      error.body.internalCode === "ZENZ-CROSS-012"
    ) {
      setServiceFailureContext(true);
      return;
    }

    const errorMessage =
      error.body?.message ||
      (error.body?.internalCode === NO_BENEFICIARY_WAS_FOUND_ERROR_CODE
        ? ERROR_MESSAGES.NO_BENEFICIARY_FOUND
        : transferOption === TRANSFER_OPTIONS.WALLET
        ? ERROR_MESSAGES.INVALID_WALLET
        : ERROR_MESSAGES.NO_BENEFICIARY_FOUND);

    // Only show error if bank is selected for bank transfers
    if (transferOption === TRANSFER_OPTIONS.WALLET || formData.bankId) {
      setFormErrors((prev) => ({
        ...prev,
        beneficiaryAccountNumber: {
          hasError: true,
          message: errorMessage,
        },
      }));
    }
  };

  const validateAmount = (amount: number) => {
    if (!amount) {
      setFormErrors((prev) => ({
        ...prev,
        amount: {
          hasError: true,
          message: ERROR_MESSAGES.AMOUNT_REQUIRED,
        },
      }));
      return;
    }

    if (amount > availableBalance) {
      setFormErrors((prev) => ({
        ...prev,
        amount: {
          hasError: true,
          message: ERROR_MESSAGES.INSUFFICIENT_BALANCE,
        },
      }));
      return;
    }

    if (
      deviceSwitchLimit?.remaining !== undefined &&
      amount > deviceSwitchLimit.remaining
    ) {
      setFormErrors((prev) => ({
        ...prev,
        amount: {
          hasError: true,
          message: `Amount exceeds your temporary device switch limit of ${formatCurrency(
            deviceSwitchLimit.limit ?? 0
          )}. You have a limit of ${formatCurrency(
            deviceSwitchLimit.remaining
          )} remaining.`,
        },
      }));
      return;
    }

    // if (amount > availableLimit) {
    //   setFormErrors((prev) => ({
    //     ...prev,
    //     amount: {
    //       hasError: true,
    //       message: `Amount entered is above your limit. You can only send ${formatCurrency(
    //         availableLimit
    //       )}`,
    //     },
    //   }));
    //   return;
    // }

    setFormErrors((prev) => ({
      ...prev,
      amount: {
        hasError: false,
        message: "",
      },
    }));
  };

  useEffect(() => {
    setSendMoneyContext({
      ...sendMoneyContext,
      accountNumber,
      accountOwner: formData.accountOwner,
      amount: formData.amount,
      description: formData.description,
      categoryId: formData.categoryId,
      categoryName: formData.categoryName,
      transferType: transferOption,
      bankId: formData.bankId,
      bankName: formData.bankName,
      beneficiaryAccountNumber: formData.beneficiaryAccountNumber,
      beneficiaryName: formData.beneficiaryName,
      nameEnquiryRef: nameEnquiryRef,
    });
  }, [
    accountNumber,
    formData.accountOwner,
    formData.amount,
    formData.description,
    formData.categoryId,
    formData.categoryName,
    formData.bankId,
    formData.bankName,
    formData.beneficiaryAccountNumber,
    formData.beneficiaryName,
    transferOption,
    nameEnquiryRef,
    setSendMoneyContext,
  ]);

  return {
    formData,
    formErrors,
    bankList,
    isLoadingBanks,
    bankListError,
    categoryList,
    availableLimit,
    availableBalance,
    accountNumber,
    transferOption,
    nameEnquiryRef,
    validatedAccount,
    saveBeneficiary,
    setFormData,
    setFormErrors,
    setTransferOption,
    setSaveBeneficiary,
    validateAccountNumber,
    validateAmount,
    resetAccountValidation,
    deviceSwitchLimit,
  };
};
